/**
 * « La restauration de ce fil n'a pas pu aboutir » — un fait qui SURVIT au
 * montage (#1332).
 *
 * Il vit hors de React et hors du scope documentaire, pour une raison de fond :
 * l'échec dont il parle est précisément celui qui empêche un montage d'exister.
 * Le loger dans un champ du montage — nullable, détruit au refroidissement,
 * absent quand le montage n'a jamais eu lieu — ferait retomber l'écran sur une
 * page blanche muette, c'est-à-dire sur l'exact symptôme que le tri-état
 * corrige.
 *
 * C'est un **ensemble indexé par conversation**, pas un pointeur : deux fils
 * peuvent échouer indépendamment, et déclarer B indisponible ne doit pas
 * innocenter A. Purgé au changement de compte (`canvasEviction`) comme toute
 * autre autorité bornée au compte connecté.
 */

import * as React from 'react';
import type { RestaurationIndisponible } from './canvasRestaurationDeBrouillon';

let registre: ReadonlyMap<string, RestaurationIndisponible> = new Map();
const auditeurs = new Set<() => void>();

function publier(suivant: ReadonlyMap<string, RestaurationIndisponible>): void {
  registre = suivant;
  for (const auditeur of [...auditeurs]) auditeur();
}

/** Déclare qu'un fil n'a pas pu être restauré, et pourquoi. */
export function declarerLaRestaurationIndisponible(etat: RestaurationIndisponible): void {
  const suivant = new Map(registre);
  suivant.set(etat.conversationId, etat);
  publier(suivant);
  console.error(`[canvas restauration] ${etat.diagnostic}`);
}

/**
 * Lève la déclaration d'un fil — au début d'une nouvelle tentative, et après
 * une restauration réussie.
 *
 * Idempotent : lever un fil non déclaré ne notifie personne, sans quoi chaque
 * ouverture de conversation ferait re-rendre tous les lecteurs.
 */
export function leverLaRestaurationIndisponible(conversationId: string): void {
  if (!registre.has(conversationId)) return;
  const suivant = new Map(registre);
  suivant.delete(conversationId);
  publier(suivant);
}

export function lireLaRestaurationIndisponible(
  conversationId: string | null,
): RestaurationIndisponible | null {
  return conversationId ? registre.get(conversationId) ?? null : null;
}

/** Changement de compte : ces déclarations appartiennent au compte qui part. */
export function oublierLesRestaurationsIndisponibles(): void {
  if (registre.size === 0) return;
  publier(new Map());
}

/**
 * Lecture React. `useSyncExternalStore` est sûr ici parce que l'instantané rendu
 * est une **référence stable** conservée dans le registre — un sélecteur qui
 * reconstruirait un objet à chaque lecture bouclerait.
 */
export function useRestaurationIndisponible(
  conversationId: string | null,
): RestaurationIndisponible | null {
  const subscribe = React.useCallback((auditeur: () => void) => {
    auditeurs.add(auditeur);
    return () => { auditeurs.delete(auditeur); };
  }, []);
  const lire = React.useCallback(
    () => lireLaRestaurationIndisponible(conversationId),
    [conversationId],
  );
  return React.useSyncExternalStore(subscribe, lire, lire);
}
